import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import DialogFrame from '../_parts/DialogFrame';

import { useParams } from 'react-router-dom';

import { DialogContent } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import Documents from '../_parts/Documents';
import {
    clearProjectDocumentsInfo,
    removeProjectDocumentsInfoItem,
} from '../../../../redux/project/documents/sliceProjectDocuments';
import {
    closeCreateNoticeDialog,
    getNoticeTypes,
    NoticeApprovalMethodEnum,
} from '../../../../redux/project/documents/sliceProjectDocumentsCreateNotice';
import CreateNoticeDialogActions from './_parts/CreateNoticeDialogActions';
import CreateNoticeContent from './_parts/CreateNoticeContent';
import { updateSelectedDocuments } from '../../../../redux/project/documents/sliceProjectSelectedDocuments';
import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { fetchViewDataAndDocuments } from '../../../../util/ProjectUtils';
import { ToastSeverity } from '../../Toast/Toast';
import {
    fetchImportDocTypes,
    fetchImportViewData,
    fetchImportViews,
} from '../../../../util/ImportUtils';
import {
    clearProjectUserList,
    getUserList,
    IProjectUserContact,
} from '../../../../redux/project/sliceProjectUserList';
import { Dayjs } from 'dayjs';
import { PickerChangeHandlerContext, DateValidationError } from '@mui/x-date-pickers';

const initialMainState: ICreateNoticeMainState = {
    taskType: '',
    comment: '',
    assignees: [],
    approvalMethod: null,
    dueDate: null,
    dueDateErrorContext: null,
};

const initialErrorState: ICreateNoticeErrorState = {
    taskType: false,
    dueDate: false,
    assignees: false,
    approvalMethod: false,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'createNoticeDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    const params = useParams();

    const { replicaId, areaName } = params;

    const { data: documents, loading: loadingInfoDocs } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );

    const { isDialogOpen, isNoticeCreated, loadingNoticeTypes, loading } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocumentsCreateNotice,
    );

    const loadingUsers = useSelector((_store: IAppReduxStore) => _store.projectUserList.loading);

    useEffect(() => {
        if (isDialogOpen) {
            dispatch(getUserList({ replicaId: params.replicaId }));
            dispatch(getNoticeTypes({ replicaId: replicaId }));
        }
    }, [isDialogOpen]);

    useEffect(() => {
        if (isNoticeCreated) {
            dispatch(
                updateSelectedDocuments({
                    replicaId,
                    areaName,
                    documentIds: [],
                }),
            );
            dispatch(clearProjectDocumentsInfo());
            if (areaName === 'import') {
                fetchImportViews(replicaId);
                fetchImportViewData(replicaId);
                fetchImportDocTypes(replicaId);
            } else {
                fetchViewDataAndDocuments(params);
            }
            dispatch(clearProjectUserList());
            enqueueSnackbar(t('toastSuccess'), {
                severity: ToastSeverity.Success,
            });
            onClose();
        }
    }, [isNoticeCreated]);

    const [mainState, setMainState] = useState<ICreateNoticeMainState>(initialMainState);
    const [showErrorState, setShowErrorState] =
        useState<ICreateNoticeErrorState>(initialErrorState);

    const handleChange = ({
        field,
        value,
    }: {
        field: string;
        value: string | number | IProjectUserContact[];
    }) => {
        setMainState(prevState => ({
            ...prevState,
            [field]: value,
        }));
        setShowErrorState(prevState => ({
            ...prevState,
            [field]: false,
        }));
    };

    const resetState = () => {
        setShowErrorState({ ...initialErrorState });
        setMainState({ ...initialMainState });
    };

    const onClose = () => {
        resetState();
        dispatch(closeCreateNoticeDialog());
    };

    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingInfoDocs || loadingUsers || loadingNoticeTypes || loading}
                    documentSelection={
                        <Documents removeDocument={removeDocument} documents={documents} />
                    }
                    main={
                        <CreateNoticeContent
                            mainState={mainState}
                            handleChange={handleChange}
                            showErrorState={showErrorState}
                            setShowErrorState={setShowErrorState}
                        />
                    }
                />
            </DialogContent>

            <CreateNoticeDialogActions
                mainState={mainState}
                showErrorState={showErrorState}
                setShowErrorState={setShowErrorState}
                onClose={onClose}
            />
        </DialogFrame>
    );
};

export interface ICreateNoticeMainState {
    taskType: string;
    comment: string;
    assignees: IProjectUserContact[];
    approvalMethod: NoticeApprovalMethodEnum;
    dueDate: Dayjs;
    dueDateErrorContext: PickerChangeHandlerContext<DateValidationError>;
}

export interface ICreateNoticeErrorState {
    taskType: boolean;
    assignees: boolean;
    approvalMethod: boolean;
    dueDate: boolean;
}
